import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatTabsModule } from '@angular/material/tabs';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AdminService } from './admin.service';
import { AuditLogDto, PermissionDto, PermissionMatrixRow, RoleDto } from './admin.models';

@Component({
  selector: 'app-admin',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatTabsModule, MatTableModule, MatProgressSpinnerModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatButtonModule, MatCheckboxModule, MatTooltipModule
  ],
  templateUrl: './admin.component.html',
  styleUrl: './admin.component.scss'
})
export class AdminComponent implements OnInit {
  private readonly admin = inject(AdminService);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly auditLogs = signal<AuditLogDto[]>([]);
  readonly roles = signal<RoleDto[]>([]);
  readonly permissions = signal<PermissionDto[]>([]);
  readonly matrix = signal<PermissionMatrixRow[]>([]);
  readonly savingRoleId = signal<string | null>(null);
  readonly creatingRole = signal(false);

  newRoleName = '';
  newRoleDescription = '';

  readonly auditColumns = ['createdAt', 'action', 'entityType', 'entityId', 'userId', 'ipAddress'];
  readonly roleColumns = ['name', 'code', 'description', 'isSystem', 'permissionCount'];

  ngOnInit(): void {
    this.loadAll();
  }

  loadAll(): void {
    this.loading.set(true);
    this.error.set(null);

    this.admin.listAuditLogs().subscribe({
      next: (page) => this.auditLogs.set(page.items ?? []),
      error: () => this.auditLogs.set([])
    });

    this.admin.buildPermissionMatrix().subscribe({
      next: ({ roles, permissions, matrix }) => {
        this.roles.set(roles);
        this.permissions.set(permissions);
        this.matrix.set(matrix);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.message || 'Failed to load roles and permissions.');
        this.loading.set(false);
      }
    });
  }

  createRole(): void {
    const name = this.newRoleName.trim();
    if (!name) {
      this.error.set('Role name is required.');
      return;
    }
    this.creatingRole.set(true);
    this.admin.createRole({
      name,
      description: this.newRoleDescription.trim() || undefined
    }).subscribe({
      next: (role) => {
        this.creatingRole.set(false);
        this.newRoleName = '';
        this.newRoleDescription = '';
        this.roles.update(list => [...list, role].sort((a, b) => a.name.localeCompare(b.name)));
        this.matrix.set(this.admin.toMatrix(this.roles(), this.permissions()));
        this.error.set(null);
      },
      error: (err) => {
        this.creatingRole.set(false);
        this.error.set(err?.message || 'Failed to create role.');
      }
    });
  }

  togglePermission(role: RoleDto, permission: PermissionDto, checked: boolean): void {
    const current = new Set((role.permissionCodes ?? role.permissions ?? []).map(c => c.toLowerCase()));
    if (checked) current.add(permission.code.toLowerCase());
    else current.delete(permission.code.toLowerCase());

    const codeByLower = new Map(this.permissions().map(p => [p.code.toLowerCase(), p.code]));
    const nextCodes = [...current].map(c => codeByLower.get(c) ?? c);

    this.savingRoleId.set(role.id);
    this.admin.setRolePermissions(role.id, nextCodes).subscribe({
      next: (updated) => {
        this.savingRoleId.set(null);
        this.roles.update(list => list.map(r => r.id === role.id ? updated : r));
        this.matrix.set(this.admin.toMatrix(this.roles(), this.permissions()));
        this.error.set(null);
      },
      error: (err) => {
        this.savingRoleId.set(null);
        this.error.set(err?.message || 'Failed to update permissions.');
        this.matrix.set(this.admin.toMatrix(this.roles(), this.permissions()));
      }
    });
  }

  modules(): string[] {
    const mods = new Set(this.permissions().map(p => p.module || 'General'));
    return [...mods].sort();
  }

  permissionsForModule(module: string): PermissionDto[] {
    return this.permissions().filter(p => (p.module || 'General') === module);
  }

  matrixRow(permissionId: string): PermissionMatrixRow | undefined {
    return this.matrix().find(m => m.permission.id === permissionId);
  }

  permissionCount(role: RoleDto): number {
    return (role.permissionCodes ?? role.permissions ?? []).length;
  }
}
